import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Truck, 
  ArrowLeft,
  X,
  Phone,
  FileText,
  Package,
  Clock,
  MapPin,
  Download,
  LogOut
} from "lucide-react";
import { toast } from "sonner";

export default function OrderDetails() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orders/${orderId}`, { headers });
      setOrder(response.data);
    } catch (e) {
      console.error("Error fetching order:", e);
      toast.error("Fehler beim Laden der Sendung");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadLabel = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/label`, {
        headers,
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `label_${order.order_number}.pdf`;
      a.click();
    } catch (e) {
      toast.error("Kein Label verfügbar");
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      "PENDING": { label: "Ausstehend", class: "bg-amber-100 text-amber-800" },
      "PICKED_UP": { label: "Abgeholt", class: "bg-blue-100 text-blue-800" },
      "IN_TRANSIT": { label: "In Zustellung", class: "bg-indigo-100 text-indigo-800" },
      "DELIVERED": { label: "Zugestellt", class: "bg-green-100 text-green-800" },
      "CANCELLED": { label: "Storniert", class: "bg-red-100 text-red-800" },
      "CUSTOMER_ABSENT": { label: "Kunde abwesend", class: "bg-red-100 text-red-800" },
      "REDELIVERY": { label: "Erneute Zustellung", class: "bg-orange-100 text-orange-800" }
    };
    const s = statusMap[status] || { label: status, class: "bg-slate-100 text-slate-800" };
    return <span className={`px-3 py-1 rounded-full text-sm font-medium ${s.class}`}>{s.label}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Truck className="h-6 w-6 text-white" />
              </div>
              <div>
                <span className="font-bold text-lg">SonderCargo.de</span>
                <span className="text-xs text-slate-400 block">Medical, Weapons and much more</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="font-medium">{user?.name}</p>
                <p className="text-xs text-slate-400 uppercase">{user?.role}</p>
              </div>
              <Button variant="ghost" size="icon" className="text-slate-300 hover:text-white" onClick={logout} data-testid="logout-btn">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="h-1 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400" />

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Row */}
        <div className="flex items-start justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate(-1)}
              className="text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                <span>SENDUNGS-NR.</span>
                <span className="text-slate-400">|</span>
                <span>STATUS</span>
              </div>
              <div className="flex items-center gap-4">
                <h1 className="text-2xl font-bold text-slate-900" data-testid="order-number">{order.order_number}</h1>
                {getStatusBadge(order.status)}
              </div>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="close-btn"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Address Cards */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Sender */}
          <Card className="border-0 shadow-sm" data-testid="sender-details">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm text-slate-500 font-medium">ABSENDER-DETAILS</CardTitle>
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">ABSENDER-ZENTRALE</span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500">Abholung am: <span className="text-blue-600 font-medium">{order.pickup_date}</span></p>
                  <p className="text-xs text-slate-400">ZEITFENSTER: {order.pickup_time_window} UHR</p>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-slate-900 uppercase">{order.sender_name}</h3>
                  <p className="text-slate-600">{order.sender_address}</p>
                  <p className="text-slate-600">{order.sender_postal_code} {order.sender_city}</p>
                  <a href={`tel:${order.sender_phone}`} className="inline-flex items-center gap-2 text-blue-600 hover:underline mt-2">
                    <Phone className="h-4 w-4" />
                    {order.sender_phone}
                  </a>
                </div>
                
                {order.sender_notes && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-xs text-amber-700 font-medium mb-1">SPEZIELLE ANWEISUNG</p>
                    <div className="flex items-start gap-2">
                      <FileText className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-800">{order.sender_notes}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Receiver */}
          <Card className="border-0 shadow-sm" data-testid="receiver-details">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm text-slate-500 font-medium">EMPFÄNGER-DETAILS</CardTitle>
                <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded">
                  {order.destination_country === "AT" ? "ÖSTERREICH" : "DEUTSCHLAND"}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500">Zustellung am: <span className="text-indigo-600 font-medium">{order.delivery_date}</span></p>
                  <p className="text-xs text-slate-400">ZEITFENSTER: {order.delivery_time_window} UHR</p>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-slate-900 uppercase">{order.receiver_name}</h3>
                  <p className="text-slate-600">{order.receiver_address}</p>
                  <p className="text-slate-600">{order.receiver_postal_code} {order.receiver_city}</p>
                  <a href={`tel:${order.receiver_phone}`} className="inline-flex items-center gap-2 text-indigo-600 hover:underline mt-2">
                    <Phone className="h-4 w-4" />
                    {order.receiver_phone}
                  </a>
                </div>
                
                {order.receiver_notes && (
                  <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                    <p className="text-xs text-indigo-700 font-medium mb-1">ZUSTELL-NOTIZ</p>
                    <div className="flex items-start gap-2">
                      <FileText className="h-4 w-4 text-indigo-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-indigo-800">{order.receiver_notes}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Card className="border-0 shadow-sm">
          <Tabs defaultValue="packages">
            <div className="border-b px-6 pt-4">
              <TabsList className="bg-transparent gap-6" data-testid="detail-tabs">
                <TabsTrigger value="packages" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none pb-3" data-testid="tab-packages">
                  PACKSTÜCKE
                </TabsTrigger>
                <TabsTrigger value="notes" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none pb-3" data-testid="tab-notes">
                  NOTIZEN ({order.history?.length || 0})
                </TabsTrigger>
                {order.label_file && (
                  <TabsTrigger value="label" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none pb-3" data-testid="tab-label">
                    LABEL
                  </TabsTrigger>
                )}
              </TabsList>
            </div>

            <TabsContent value="packages" className="p-6">
              <div className="grid lg:grid-cols-2 gap-8">
                {/* Packages List */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-4">Packstücke</h3>
                  <div className="space-y-4">
                    {order.packages?.map((pkg, idx) => (
                      <div key={idx} className={`border rounded-lg p-4 ${
                        pkg.status === "error" ? "border-red-300 bg-red-50" :
                        pkg.status === "delivered" ? "border-green-300 bg-green-50" :
                        pkg.status === "picked_up" ? "border-blue-300 bg-blue-50" :
                        ""
                      }`} data-testid={`package-detail-${idx}`}>
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-medium">#{idx + 1} - {pkg.category}</span>
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                            pkg.status === "delivered" ? "bg-green-100 text-green-700" :
                            pkg.status === "picked_up" ? "bg-blue-100 text-blue-700" :
                            pkg.status === "error" ? "bg-red-100 text-red-700" :
                            "bg-slate-100 text-slate-600"
                          }`}>
                            {pkg.status === "delivered" ? "Zugestellt" :
                             pkg.status === "picked_up" ? "Abgeholt" :
                             pkg.status === "error" ? "Fehler" :
                             "Ausstehend"}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-slate-500 text-xs uppercase">Identnummer</p>
                            <p className="font-medium">{pkg.ident_number || "N/A"}</p>
                          </div>
                          <div>
                            <p className="text-slate-500 text-xs uppercase">Gewicht</p>
                            <p className="font-medium">{pkg.weight?.toFixed(2)} kg</p>
                          </div>
                          <div>
                            <p className="text-slate-500 text-xs uppercase">Maße (L×B×H)</p>
                            <p className="font-medium">
                              {pkg.length && pkg.width && pkg.height 
                                ? `${pkg.length} × ${pkg.width} × ${pkg.height} cm`
                                : "N/A"}
                            </p>
                          </div>
                          {pkg.status_updated_by && (
                            <div>
                              <p className="text-slate-500 text-xs uppercase">Aktualisiert von</p>
                              <p className="font-medium">{pkg.status_updated_by}</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Error display */}
                        {pkg.status === "error" && (
                          <div className="mt-3 p-3 bg-red-100 border border-red-200 rounded-lg">
                            <p className="text-sm font-medium text-red-800">
                              Fehler: {
                                pkg.error_type === "not_available" ? "Paket nicht verfügbar" :
                                pkg.error_type === "misrouting" ? "Fehlrouting" :
                                pkg.error_type === "damaged" ? "Beschädigt" :
                                pkg.error_type === "other" ? "Sonstiges" : 
                                pkg.error_type || "Unbekannter Fehler"
                              }
                            </p>
                            {pkg.error_info && (
                              <p className="text-sm text-red-700 mt-1">{pkg.error_info}</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Timeline */}
                <div>
                  <h3 className="font-semibold text-slate-900 mb-4">Sendungsverlauf</h3>
                  <div className="relative">
                    {order.history?.slice().reverse().map((event, idx) => {
                      const isStatus = event.type === "status" || event.event?.includes("Sendung");
                      const isInfo = event.type === "info" || event.event?.startsWith("INFO:");
                      const isPrice = event.type === "price" || event.event?.includes("Preis");
                      const isDelivered = event.event?.includes("zugestellt") || event.event?.includes("Zugestellt");
                      const isError = event.type === "error" || event.event?.startsWith("FEHLER");
                      const isPackageStatus = event.type === "package_status";
                      const isPickedUp = event.event?.includes("Abgeholt");
                      
                      return (
                        <div key={idx} className="flex gap-4 mb-4 relative" data-testid={`history-${idx}`}>
                          <div className="relative">
                            <div className={`w-4 h-4 rounded-full z-10 relative flex items-center justify-center ${
                              isError ? "bg-red-500" :
                              isDelivered ? "bg-green-500" :
                              isPickedUp ? "bg-blue-500" :
                              isStatus ? "bg-blue-500" :
                              isInfo ? "bg-amber-500" :
                              isPrice ? "bg-purple-500" :
                              isPackageStatus ? "bg-cyan-500" :
                              "bg-slate-400"
                            }`}>
                              {isDelivered && (
                                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                              {isError && (
                                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            {idx < order.history.length - 1 && (
                              <div className="absolute top-4 left-2 w-0.5 h-full bg-slate-200 -translate-x-1/2" />
                            )}
                          </div>
                          <div className={`flex-1 pb-2 p-3 rounded-lg ${
                            isError ? "bg-red-50 border border-red-200" :
                            isDelivered ? "bg-green-50 border border-green-200" :
                            isPickedUp ? "bg-blue-50 border border-blue-200" :
                            isInfo ? "bg-amber-50 border border-amber-200" :
                            isPrice ? "bg-purple-50 border border-purple-200" :
                            isPackageStatus ? "bg-cyan-50 border border-cyan-200" :
                            "bg-slate-50"
                          }`}>
                            <div className="flex items-center justify-between mb-1">
                              <p className={`text-sm font-medium ${
                                isError ? "text-red-800" :
                                isDelivered ? "text-green-800" :
                                isPickedUp ? "text-blue-800" :
                                isInfo ? "text-amber-800" :
                                isPrice ? "text-purple-800" :
                                isPackageStatus ? "text-cyan-800" :
                                "text-slate-900"
                              }`}>
                                {event.event?.replace("INFO: ", "")}
                              </p>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                isError ? "bg-red-200 text-red-800" :
                                isDelivered ? "bg-green-200 text-green-800" :
                                isPickedUp ? "bg-blue-200 text-blue-800" :
                                isInfo ? "bg-amber-200 text-amber-800" :
                                isStatus ? "bg-blue-200 text-blue-800" :
                                isPackageStatus ? "bg-cyan-200 text-cyan-800" :
                                isPrice ? "bg-purple-200 text-purple-800" :
                                "bg-slate-200 text-slate-600"
                              }`}>
                                {event.station || "SYSTEM"}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500">
                              {new Date(event.timestamp).toLocaleDateString('de-DE')} um {new Date(event.timestamp).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
                              {event.user && ` • ${event.user}`}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    {(!order.history || order.history.length === 0) && (
                      <div className="text-center py-8 text-slate-400">
                        <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>Noch keine Verlaufsdaten</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="notes" className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Vollständiger Verlauf</h3>
              <div className="space-y-3">
                {order.history?.slice().reverse().map((event, idx) => {
                  const isInfo = event.type === "info" || event.event?.startsWith("INFO:");
                  const isStatus = event.type === "status" || event.event?.includes("Sendung");
                  const isPrice = event.type === "price";
                  
                  return (
                    <div key={idx} className={`p-4 rounded-lg border ${
                      isInfo ? "bg-amber-50 border-amber-200" :
                      isStatus ? "bg-blue-50 border-blue-200" :
                      isPrice ? "bg-purple-50 border-purple-200" :
                      "bg-slate-50 border-slate-200"
                    }`} data-testid={`note-${idx}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <p className={`font-medium ${
                            isInfo ? "text-amber-900" :
                            isStatus ? "text-blue-900" :
                            isPrice ? "text-purple-900" :
                            "text-slate-900"
                          }`}>
                            {event.event?.replace("INFO: ", "")}
                          </p>
                          {event.user && (
                            <p className="text-xs text-slate-500 mt-1">
                              Bearbeiter: {event.user}
                            </p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-medium text-slate-700">
                            {new Date(event.timestamp).toLocaleDateString('de-DE')}
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(event.timestamp).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
                          </p>
                          <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded ${
                            isInfo ? "bg-amber-200 text-amber-800" :
                            isStatus ? "bg-blue-200 text-blue-800" :
                            isPrice ? "bg-purple-200 text-purple-800" :
                            "bg-slate-200 text-slate-600"
                          }`}>
                            {event.station || "SYSTEM"}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {(!order.history || order.history.length === 0) && (
                  <div className="text-center py-8 text-slate-400">
                    <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Noch keine Einträge</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {order.label_file && (
              <TabsContent value="label" className="p-6">
                <div className="text-center">
                  <Package className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="font-semibold text-slate-900 mb-2">Versandlabel verfügbar</h3>
                  <p className="text-slate-500 mb-4">Das Label wurde hochgeladen und kann heruntergeladen werden.</p>
                  <Button onClick={handleDownloadLabel} className="bg-blue-600 hover:bg-blue-700" data-testid="download-label-btn">
                    <Download className="h-4 w-4 mr-2" />
                    Label herunterladen
                  </Button>
                </div>
              </TabsContent>
            )}
          </Tabs>
        </Card>

        {/* Price Summary */}
        <Card className="border-0 shadow-sm mt-6 bg-slate-900 text-white" data-testid="price-summary">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold">Preisübersicht</h3>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm max-w-md">
                  <span className="text-slate-400">Grundpreis ({order.packages?.length || 1}× Paket):</span>
                  <span className="text-right">{order.base_price?.toFixed(2)}€</span>
                  
                  {order.surcharge_details?.express > 0 && (
                    <>
                      <span className="text-slate-400">Express-Zuschlag:</span>
                      <span className="text-right text-green-400">+{order.surcharge_details.express.toFixed(2)}€</span>
                    </>
                  )}
                  
                  {order.surcharge_details?.saturday > 0 && (
                    <>
                      <span className="text-slate-400">Samstags-Zuschlag:</span>
                      <span className="text-right text-purple-400">+{order.surcharge_details.saturday.toFixed(2)}€</span>
                    </>
                  )}
                  
                  {order.surcharge_details?.volume > 0 && (
                    <>
                      <span className="text-slate-400">Volumen-Zuschlag:</span>
                      <span className="text-right text-amber-400">+{order.surcharge_details.volume.toFixed(2)}€</span>
                    </>
                  )}
                  
                  {order.surcharge_details?.weight > 0 && (
                    <>
                      <span className="text-slate-400">Gewichts-Zuschlag:</span>
                      <span className="text-right text-orange-400">+{order.surcharge_details.weight.toFixed(2)}€</span>
                    </>
                  )}
                  
                  {order.surcharge_details?.pickup_time > 0 && (
                    <>
                      <span className="text-slate-400">Zeitfenster-Zuschlag:</span>
                      <span className="text-right text-cyan-400">+{order.surcharge_details.pickup_time.toFixed(2)}€</span>
                    </>
                  )}
                  
                  {/* Fallback if no surcharge_details but surcharges exist */}
                  {!order.surcharge_details && order.surcharges > 0 && (
                    <>
                      <span className="text-slate-400">Zuschläge (gesamt):</span>
                      <span className="text-right text-green-400">+{order.surcharges.toFixed(2)}€</span>
                    </>
                  )}
                  
                  {order.price_override && (
                    <>
                      <span className="text-amber-400 col-span-2 mt-2 border-t border-slate-700 pt-2">
                        ⚠ Preis überschrieben: {order.price_override_reason}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="text-right ml-6">
                <p className="text-sm text-slate-400">Netto</p>
                <p className="text-4xl font-bold">{(order.price_override || order.total_price)?.toFixed(2)}€</p>
                <p className="text-sm text-slate-400 mt-1">
                  inkl. 19% MwSt.: <span className="text-white font-semibold">{((order.price_override || order.total_price) * 1.19)?.toFixed(2)}€</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
